import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { PasswordInput } from '@xxtract/ui';
import { useAuth } from '../hooks/useAuth';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { LanguageSelector } from './LanguageSelector';

// Bewust ruim: het scherm hoeft alleen een duidelijke vergissing te herkennen ("geen-adres").
// Of het adres echt bestaat blijkt pas bij het inloggen, en dat is de enige betrouwbare toets.
const LIJKT_OP_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function LoginForm() {
  const { t } = useTranslation();
  const { loginMutation } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Story 2-1: een melding onder het veld, maar pas nadat de bezoeker het veld heeft verlaten.
  // Meteen bij het typen meldden dat het adres nog niet klopt is hinderlijk; pas bij verlaten is
  // het behulpzaam.
  const [emailAangeraakt, setEmailAangeraakt] = useState(false);
  const [wachtwoordAangeraakt, setWachtwoordAangeraakt] = useState(false);

  const emailFout = !emailAangeraakt
    ? null
    : email.trim() === ''
      ? 'auth.validation.emailRequired'
      : LIJKT_OP_EMAIL.test(email.trim())
        ? null
        : 'auth.validation.emailInvalid';
  const wachtwoordFout = wachtwoordAangeraakt && password === ''
    ? 'auth.validation.passwordRequired'
    : null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  };

  return (
    <form onSubmit={handleSubmit} data-testid="login-form" className="w-full">
      <h1
        className="mb-1"
        style={{
          fontSize: '22px',
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: 'var(--primary)',
        }}
      >
        {t('auth.login')}
      </h1>
      <p
        className="mb-7"
        style={{ fontSize: '13px', color: 'var(--foreground-muted)' }}
      >
        {t('auth.loginSubtitle')}
      </p>

      {loginMutation.isError && (
        <ApiErrorMessage error={loginMutation.error} />
      )}

      <div className="mb-3.5 flex flex-col gap-[5px]">
        <label
          htmlFor="email"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground)' }}
        >
          {t('auth.email')}
        </label>
        <input
          id="email"
          data-testid="login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setEmailAangeraakt(true)}
          aria-invalid={emailFout != null}
          aria-describedby={emailFout == null ? undefined : 'email-error'}
          required
          autoComplete="email"
          className="rounded-lg px-3 py-2 text-[13px] outline-none"
          style={{
            border: '1px solid var(--border)',
            backgroundColor: 'var(--background)',
            color: 'var(--foreground)',
          }}
          placeholder="naam@bedrijf.nl"
        />
        {emailFout != null && (
          <p
            id="email-error"
            data-testid="email-error"
            role="alert"
            className="text-xs"
            style={{ color: 'var(--destructive)' }}
          >
            {t(emailFout)}
          </p>
        )}
      </div>

      <div className="mb-3.5 flex flex-col gap-[5px]">
        <label
          htmlFor="password"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground)' }}
        >
          {t('auth.password')}
        </label>
        <PasswordInput
          id="password"
          data-testid="login-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onBlur={() => setWachtwoordAangeraakt(true)}
          aria-invalid={wachtwoordFout != null}
          aria-describedby={wachtwoordFout == null ? undefined : 'password-error'}
          required
          autoComplete="current-password"
          showLabel={t('auth.showPassword')}
          hideLabel={t('auth.hidePassword')}
          className="rounded-lg px-3 py-2 text-[13px] outline-none"
          style={{
            border: '1px solid var(--border)',
            backgroundColor: 'var(--background)',
            color: 'var(--foreground)',
          }}
          placeholder="••••••••"
        />
        {wachtwoordFout != null && (
          <p
            id="password-error"
            data-testid="password-error"
            role="alert"
            className="text-xs"
            style={{ color: 'var(--destructive)' }}
          >
            {t(wachtwoordFout)}
          </p>
        )}
      </div>

      <button
        type="submit"
        data-testid="login-submit"
        disabled={loginMutation.isPending}
        className="mt-2 w-full rounded-lg py-[11px] px-4 text-sm font-medium text-white transition-all duration-100 disabled:opacity-60"
        style={{
          backgroundColor: 'var(--primary)',
          border: '1px solid var(--primary)',
        }}
      >
        {loginMutation.isPending ? t('common.loading') : t('auth.login')}
      </button>

      <Link
        to="/reset-password"
        data-testid="forgot-password-link"
        className="mt-4 block text-center text-xs"
        style={{ color: 'var(--secondary)' }}
      >
        {t('auth.forgotPassword')}
      </Link>

      {/* Language selector — below form with separator */}
      <div
        className="mt-6 flex justify-center pt-4"
        style={{ borderTop: '1px solid var(--border-soft)' }}
      >
        <LanguageSelector />
      </div>
    </form>
  );
}
